import { useUserSession } from '@solinkcorp/authentication';
import {
  Modal,
  type ModalAction,
  type ModalProps,
  PhoneNumberField,
} from '@solinkcorp/solink-components';
import { useEffect, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import { alarmCountries, validatePhoneNumber } from 'utils/AlarmContactUtils';
import { normalizePhoneNumber } from 'utils/phoneNumberFormatUtil';
import { localStorageVerifEyeKey, type VerifyEyeLocalStorage } from './useVerifEyePhoneModal';

export interface VerifEyePhoneModalProps {
  verifEyePhoneModalOpen: boolean;
  setVerifEyePhoneModalOpen: (modalOpen: boolean) => void;
}

const twelveHoursInMilli = 1000 * 60 * 60 * 12;

export function VerifEyePhoneModal(props: VerifEyePhoneModalProps) {
  const locale = useLocale();
  const user = useUserSession();

  const [verifEyePhoneNumber, setVerifEyePhoneNumber] = useState('');
  const [verifEyeModalSaveEnabled, setVerifEyeModalSaveEnabled] = useState(false);

  const { setVerifEyePhoneModalOpen: setVerifEyeModalOpen } = props;

  useEffect(() => {
    let isVerifEyeLocalStorageFresh = false;
    let defaultVerifEyePhoneNumber = user.profile.phoneNumber || '';
    const localStorageVerifEyeData = localStorage.getItem(localStorageVerifEyeKey);
    if (localStorageVerifEyeData) {
      const verifEyeData: VerifyEyeLocalStorage = JSON.parse(localStorageVerifEyeData);
      if (user.profile.email === verifEyeData.email) {
        defaultVerifEyePhoneNumber = verifEyeData.phoneNumber;

        // Check for old format
        if (defaultVerifEyePhoneNumber.startsWith('(')) {
          // remove non-digit characters, add +1
          defaultVerifEyePhoneNumber = '+1' + normalizePhoneNumber(defaultVerifEyePhoneNumber);
        }

        // Prompt for phone number every 12 hours
        if (Date.now() - verifEyeData.timestamp < twelveHoursInMilli) {
          isVerifEyeLocalStorageFresh = true;
        }
      }
    }

    const validPhoneNumber = validatePhoneNumber(defaultVerifEyePhoneNumber);
    if (validPhoneNumber) {
      setVerifEyePhoneNumber(defaultVerifEyePhoneNumber);
      setVerifEyeModalSaveEnabled(true);
    }

    if (!validPhoneNumber || !isVerifEyeLocalStorageFresh) {
      setVerifEyeModalOpen(true);
    }
  }, [user, setVerifEyeModalOpen]);

  const verifEyeModalSaveOnClick = () => {
    const localStorageVerifyEyePhoneNumber: VerifyEyeLocalStorage = {
      email: user.profile.email,
      phoneNumber: verifEyePhoneNumber,
      timestamp: Date.now(),
    };

    localStorage.setItem(localStorageVerifEyeKey, JSON.stringify(localStorageVerifyEyePhoneNumber));

    props.setVerifEyePhoneModalOpen(false);
  };

  const onVerifEyePhoneNumberChange = (newPhoneNumber: string, data: { valid: boolean }) => {
    setVerifEyePhoneNumber(newPhoneNumber);
    setVerifEyeModalSaveEnabled(data.valid);
  };

  const verifEyeModalConfirmAction: ModalAction = {
    text: locale.general.save,
    onClick: verifEyeModalSaveOnClick,
    disabled: !verifEyeModalSaveEnabled,
  };

  const verifEyePhoneModalProps: ModalProps = {
    autoFocus: false,
    bodyStyle: { padding: 30 },
    closeButton: false,
    isOpen: props.verifEyePhoneModalOpen,
    title: locale.verificationCentre.verifierPhoneNumber,
    colorMode: 'dark' as const,
    type: 'narrow' as const,
    actions: [verifEyeModalConfirmAction],
  };

  return (
    <Modal {...verifEyePhoneModalProps}>
      <div className="verifeye-modal-text">{locale.verificationCentre.authoritiesMayContact}</div>
      <div className="verifeye-modal-text">{locale.verificationCentre.enterPhoneNumber}</div>
      <div className="verifeye-phone-number">
        <PhoneNumberField
          colorMode="dark"
          errorText={locale.alarms.phoneNumberError}
          updatePhoneNumber={onVerifEyePhoneNumberChange}
          phoneNumber={verifEyePhoneNumber}
          required
          onlyCountries={alarmCountries}
          errorWhenNotInOnlyCountriesMessage={locale.alarms.restrictedPhoneNumberCountryCodeError}
        />
      </div>
    </Modal>
  );
}
